import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ACTION_LABELS = {
  'exa_sweep': 'Signal sweep',
  'exa_sweep_test': 'Signal sweep',
  'claude_research_sweep': 'Signal sweep',
  'claude_research': 'Company research',
  'claude_sequence': 'Sequence generation',
  'discovery_generate': 'Discovery brief',
  'find_companies': 'Company search',
  'find_contacts': 'Contact search',
  'generate_icp': 'ICP generation',
  'enrich_contact': 'Contact enrichment',
  'topup': 'Top-up',
  'topup_manual_recovery': 'Top-up (manual)',
  'monthly': 'Monthly allocation',
  'icp-company-import': 'ICP company import',
  'signal-sweep': 'Signal sweep',
  'signal-detect': 'Signal sweep',
  'sequence-generate': 'Sequence generation',
  'enrichment': 'Contact enrichment',
  'monthly-allocation': 'Monthly allocation',
  'monthly-reset': 'Monthly reset',
  'cache-hit': 'Cache hit',
  'refund': 'Refund',
};

const SOURCE_LABELS = {
  'topup': 'Stripe top-up',
  'monthly': 'Plan allocation',
  'admin': 'Admin adjustment',
  'rejected': 'No charge',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function actionLabel(action) {
  if (!action) return '';
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function sourceLabel(source) {
  if (!source) return '';
  return SOURCE_LABELS[source] || source.replace(/_/g, ' ');
}

function downloadCsv(rows, filename) {
  const headers = ['When', 'Action', 'Source', 'Change', 'Balance'];
  const lines = [headers.join(',')];
  for (const tx of rows) {
    const cells = [
      formatDate(tx.created_at),
      actionLabel(tx.action),
      sourceLabel(tx.source),
      tx.amount_delta,
      tx.balance_after,
    ].map(v => {
      const s = String(v == null ? '' : v);
      return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g, '""') + '"' : s;
    });
    lines.push(cells.join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Usage({ embedded = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  async function authedFetch(url) {
    const token = await getToken();
    return fetch(url, { headers: { 'Authorization': 'Bearer ' + token } });
  }
  const [client, setClient] = useState(null);
  const [credits, setCredits] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState('all');

  async function load() {
    setLoading(true);
    try {
      // Resolve account from workspace, then read account-level credits + transactions
      const clientRes = await fetch(API + '/clients/' + id).then(r => r.json());
      setClient(clientRes);
      const accountId = clientRes?.account_id;
      if (!accountId) {
        // Legacy fallback — workspace without account
        const [creditsRes, txRes] = await Promise.all([
          creditAwareFetch(API + '/clients/' + id + '/credits').then(r => r.json()),
          creditAwareFetch(API + '/clients/' + id + '/credit-transactions?limit=' + limit).then(r => r.json()),
        ]);
        setCredits(creditsRes);
        setTransactions(Array.isArray(txRes) ? txRes : []);
        return;
      }
      const [creditsRes, txRes] = await Promise.all([
        authedFetch(API + '/accounts/' + accountId + '/credits').then(r => r.json()),
        authedFetch(API + '/accounts/' + accountId + '/credit-transactions?limit=' + limit).then(r => r.json()),
      ]);
      setCredits(creditsRes.account || creditsRes);
      setTransactions(Array.isArray(txRes) ? txRes : []);
    } catch (e) {
      console.error('Usage load failed:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, limit]);

  const filtered = useMemo(() => {
    if (filter === 'debits') return transactions.filter(t => t.amount_delta < 0);
    if (filter === 'credits') return transactions.filter(t => t.amount_delta > 0);
    if (filter === 'zero') return transactions.filter(t => t.amount_delta === 0);
    if (filter === 'nonzero') return transactions.filter(t => t.amount_delta !== 0);
    return transactions;
  }, [transactions, filter]);

  if (loading) return <div style={{ padding: '2rem' }}><div style={{ height: '60px', background: '#EEECEA', borderRadius: '10px', marginBottom: '0.75rem' }} /></div>;

  const monthlyPct = credits && credits.monthly_allocation > 0
    ? Math.round((credits.monthly_remaining / credits.monthly_allocation) * 100)
    : 0;

  const thStyle = { textAlign: 'left', padding: '0.7rem 1rem', fontWeight: '500', color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const thRight = { ...thStyle, textAlign: 'right' };

  return (
    <div style={embedded ? { boxSizing: 'border-box' } : { padding: '2rem', maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>
      <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
        ← Back to workspace
      </button>

      <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.2rem', color: '#1A1A1A' }}>Usage</h1>
      <p style={{ margin: '0 0 2rem', fontSize: '0.83rem', color: '#888' }}>{client && client.name ? client.name : ''}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '2rem' }}>
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account pot</p>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '500', color: '#1A1A1A' }}>{credits ? (credits.pot ?? credits.monthly_remaining ?? 0).toLocaleString('en-GB') : 0}</p>
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem', color: '#888' }}>monthly allocation: {credits ? (credits.monthly_allocation || 0).toLocaleString('en-GB') : 0}</p>
        </div>
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Top-up balance</p>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '500', color: '#1A1A1A' }}>{credits ? (credits.topup ?? credits.topup_remaining ?? 0).toLocaleString('en-GB') : 0}</p>
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem', color: '#888' }}>rolls over month-to-month</p>
        </div>
        <div style={{ background: '#8B1A1A', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total available</p>
          <div>
            <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '500', color: '#FFF' }}>{credits ? ((credits.pot ?? credits.monthly_remaining ?? 0) + (credits.topup ?? credits.topup_remaining ?? 0)).toLocaleString('en-GB') : 0}</p>
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-topup-modal', { detail: { clientId: id } }))}
              style={{ marginTop: '0.6rem', background: '#FFF', color: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.8rem', fontWeight: '500', cursor: 'pointer' }}>
              Top up
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: '500', margin: 0, color: '#1A1A1A' }}>Recent transactions</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}>
            <option value="all">All transactions</option>
            <option value="nonzero">Hide zero changes</option>
            <option value="debits">Debits only</option>
            <option value="credits">Credits only</option>
            <option value="zero">Zero changes only</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value))}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer' }}>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
          </select>
          <button onClick={() => downloadCsv(filtered, 'usage-' + (client && client.name ? client.name : id) + '-' + new Date().toISOString().split('T')[0] + '.csv')}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.85rem', border: '0.5px solid #E8E4E0', borderRadius: '6px', background: '#FFF', cursor: 'pointer', fontWeight: '500', color: '#1A1A1A' }}>
            Export CSV
          </button>
        </div>
      </div>

      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
          <thead>
            <tr style={{ background: '#FAF8F6' }}>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>Source</th>
              <th style={thRight}>Change</th>
              <th style={thRight}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>No transactions match this filter</td></tr>
            )}
            {filtered.map(tx => {
              const isCredit = tx.amount_delta > 0;
              const isZero = tx.amount_delta === 0;
              return (
                <tr key={tx.id} style={{ borderTop: '0.5px solid #F0EDE9', opacity: isZero ? 0.55 : 1 }}>
                  <td style={{ padding: '0.7rem 1rem', color: '#555', whiteSpace: 'nowrap' }}>{formatDate(tx.created_at)}</td>
                  <td style={{ padding: '0.7rem 1rem', color: '#1A1A1A' }}>{actionLabel(tx.action)}</td>
                  <td style={{ padding: '0.7rem 1rem', color: '#888' }}>{sourceLabel(tx.source)}</td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'right', fontWeight: '500', color: isZero ? '#888' : isCredit ? '#276749' : '#8B1A1A', whiteSpace: 'nowrap' }}>
                    {isCredit ? '+' : ''}{tx.amount_delta}
                  </td>
                  <td style={{ padding: '0.7rem 1rem', textAlign: 'right', color: '#555' }}>{tx.balance_after}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
